// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { SyntheticEvent, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useRouter } from 'next/router';

import {
  borderRadius,
  fontSize,
  fontWeight,
  hideScrollbar,
  isMediaMax,
  mediaMax,
  spacing,
  zIndex,
} from 'presentations/views/styles';
import { TimeZoneListItem } from 'presentations/api/schema/time_zone';
import { DateTime, formatToDate } from 'infras/datetime';
import { CalendarViewType } from 'domains/models/event_type/calendarViewType';
import UserEventTypeTimeList from 'presentations/views/components/organisms/UserEventTypeTimeList';
import {
  black,
  gray01,
  gray02,
  gray04,
  white,
} from 'presentations/views/styles/color';
import DatePicker from 'presentations/views/components/organisms/features/eventType/calendarView/shared/components/DatePicker';
import TextField from 'presentations/views/components/atoms/TextField';
import Icon from 'presentations/views/components/atoms/Icon';
import { Trail } from 'presentations/views/components/atoms/Transition';
import { StartWeekDay } from 'domains/models/event_type/startWeekDay';
import CalendarUnavailableButton from '../../../../../molecules/CalendarUnavailableButton';
import { useEventTypeDateForm } from '../EventTypeDateForm/hooks';
import { TimeList } from 'presentations/api/helpers/event_type';
import TimeZoneListPopover from 'presentations/views/components/organisms/TimeZoneListPopover';

interface Props {
  isLoading?: boolean;
  isLoadingMonth?: boolean;
  isOpen: boolean;
  onChangeMonth?: any;
  selectedDate: DateTime | null;
  selectedDatetime: DateTime | null;
  onDateSelect: (selected: DateTime) => void;
  onTimeSelect?: Function;
  defaultDate?: DateTime | null;
  timeZones: TimeZoneListItem[];
  timeZone: string;
  updateTimeZone?: (timeZone: string) => void;
  availableDays: boolean[] | null;
  timeList: TimeList;
  color: string;
  isLightColor: boolean;
  calendarViewType: CalendarViewType;
  isRevealGuestUser: boolean;
  isShowViewerCalendar: boolean;
  isHostDiagnosticMode?: boolean;
  onClickBlockTime?: Function;
  startWeekDay: StartWeekDay;
  isFetching: boolean;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);

  const { state, actions } = useEventTypeDateForm(props);

  const router = useRouter();
  const isTablet = isMediaMax('tablet');

  const isFillColor =
    props.calendarViewType === CalendarViewType.TwoColumnMonthAccent;

  const [timeZoneAnchor, setTimeZoneAnchor] = useState<HTMLElement | null>(
    null,
  );

  const onCloseTimeZone = () => {
    setTimeZoneAnchor(null);
  };

  const onClickTimeZone = (e: SyntheticEvent<HTMLDivElement>) => {
    setTimeZoneAnchor(e.currentTarget);
  };

  const onSelectTimeZone = (name: string) => {
    if (!props.updateTimeZone) return;
    props.updateTimeZone(name);
    onCloseTimeZone();
  };

  function renderDay(
    day: DateTime,
    selectedDate: DateTime,
    dayComponent: JSX.Element,
  ) {
    const isSelected =
      selectedDate &&
      day.year === selectedDate.year &&
      day.month === selectedDate.month &&
      day.day === selectedDate.day;
    if (!props.availableDays) {
      return (
        <NoTimeListDay
          className="MuiButtonBase-root"
          color={props.color}
          isLightColor={props.isLightColor}
          isFillColor={isFillColor}
          isSelected={isSelected}
        >
          {day.day}
        </NoTimeListDay>
      );
    }
    const index = day.day - 1;
    if (!props.availableDays[index]) {
      return (
        <NoTimeListDay
          className="MuiButtonBase-root"
          color={props.color}
          isLightColor={props.isLightColor}
          isFillColor={isFillColor}
          isSelected={isSelected}
          onClick={
            props.isHostDiagnosticMode
              ? () => props.onDateSelect(day)
              : undefined
          }
          isHostDiagnosticMode={props.isHostDiagnosticMode}
        >
          {day.day}
        </NoTimeListDay>
      );
    } else {
      return dayComponent;
    }
  }

  return (
    <Container calendarViewType={props.calendarViewType}>
      <DateContainer
        isFillColor={isFillColor}
        color={props.color}
        isLightColor={props.isLightColor}
      >
        <DatePickerContainer>
          <StyledTrail isOpen={props.isOpen}>
            <Picker isFillColor={isFillColor} isLightColor={props.isLightColor}>
              <DatePicker
                datePickerRef={state.datePickerRef}
                isLoading={props.isLoadingMonth || props.isLoading}
                isDisabled={
                  props.isLoadingMonth || props.isLoading || !state.isAvailable
                }
                isAvailable={state.isAvailable}
                defaultDate={props.defaultDate}
                onSelect={props.onDateSelect}
                value={props.selectedDate}
                locale={router.locale}
                disablePast={!props.isHostDiagnosticMode}
                onChangeMonth={props.onChangeMonth}
                shouldDisableDate={actions.shouldDisableDate}
                color={props.color}
                startWeekDay={props.startWeekDay}
                isLightColor={props.isLightColor}
                renderDay={props.isHostDiagnosticMode ? renderDay : undefined}
                isSetMinDateUndefined
              />
              {!props.isFetching && !state.isAvailable && (
                <CalendarUnavailableButtonLayout>
                  <CalendarUnavailableButton
                    color={props.color}
                    isWeeklyView={false}
                    onClick={actions.onClickCalendarUnavailableButton}
                  />
                </CalendarUnavailableButtonLayout>
              )}
            </Picker>
            <TimeZoneContainer onClick={onClickTimeZone}>
              {!props.isLoading && (
                <TimeZoneIcon
                  name="access_time"
                  isFillColor={isFillColor}
                  isLightColor={props.isLightColor}
                />
              )}
              <TimeZoneSelect
                width={160}
                isLoading={props.isLoading}
                name="timeZone"
                size="small"
                variant="standard"
                value={props.timeZone}
                color={props.color}
                isFillColor={isFillColor}
                isLightColor={props.isLightColor}
              />
            </TimeZoneContainer>
          </StyledTrail>
        </DatePickerContainer>
      </DateContainer>
      {!isTablet && (
        <PickerContainer>
          <StyledTimeList>
            <TimeListContainer isFillColor={isFillColor}>
              <DateSelected>
                {props.selectedDate?.toFormat(t('date format'))}
              </DateSelected>
              <UserEventTypeTimeList
                selectedDate={props.selectedDate}
                selectedDatetime={props.selectedDatetime}
                onSelect={props.onTimeSelect}
                timeList={props.timeList}
                isLoading={props.isLoading}
                timeZone={props.timeZone}
                color={props.color}
                isLightColor={props.isLightColor}
                isFullHeight
                isRevealGuestUser={props.isRevealGuestUser}
                isShowViewerCalendar={props.isShowViewerCalendar}
                isHostDiagnosticMode={props.isHostDiagnosticMode}
                onClickBlockTime={props.onClickBlockTime}
              />
            </TimeListContainer>
          </StyledTimeList>
        </PickerContainer>
      )}
      {timeZoneAnchor && (
        <TimeZoneListPopover
          title={t('Edit time zone')}
          anchor={timeZoneAnchor}
          onClose={onCloseTimeZone}
          onSelect={onSelectTimeZone}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        />
      )}
    </Container>
  );
}

const Container = styled.div<{
  calendarViewType: CalendarViewType;
}>`
  width: 100%;
  height: 491px;
  display: flex;
  justify-content: center;
  align-items: stretch;
  isolation: isolate;

  border-radius: ${borderRadius(24)};
  overflow: hidden;
  border: 1px solid ${gray04};

  ${mediaMax.tablet} {
    height: initial;
  }
  ${mediaMax.smartphone} {
    border-radius: ${borderRadius(12)};
  }

  ${(props) => {
    if (props.calendarViewType === CalendarViewType.TwoColumnMonth) {
      return `
        border: none;
        border-radius: 0;
      `;
    }
  }}
`;

const CalendarUnavailableButtonLayout = styled.div`
  position: absolute;
  top: calc(50% - 42.75px);
`;
const StyledTrail = styled(Trail)`
  overflow: hidden;
`;
const DateContainer = styled.div<{
  isFillColor: boolean;
  color: string;
  isLightColor: boolean;
}>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow: auto;
  flex: 1;
  ${hideScrollbar};

  ${(props) =>
    props.isFillColor &&
    css`
      background-color: ${props.color};
    `}

  ${mediaMax.tablet} {
    width: 100%;

    ${({ isFillColor }) =>
      isFillColor
        ? css`
            padding: ${spacing(28)} 0;
          `
        : css`
            border-right: none;
          `}
  }
`;
const Picker = styled.div<{
  isFillColor: boolean;
  isLightColor: boolean;
}>`
  display: flex;
  justify-content: center;
  margin-bottom: ${spacing(16)};

  ${(props) =>
    props.isFillColor &&
    css`
      .MuiPickerStaticWrapper-root {
        color: ${props.isLightColor ? black(props) : white(props)};
      }

      // 全体
      .MuiPickerStaticWrapper-content {
        background-color: initial;
      }

      // ヘッダ
      .MuiIconButton-root {
        color: ${props.isLightColor ? black(props) : white(props)};
        .Mui-disabled {
          /* color: ${
            props.isLightColor
              ? 'rgba(0, 0, 0, 0.26)'
              : 'rgba(255, 255, 255, 0.26)'
          }; */
        }
      }

      // 曜日
      span[role='columnheader'] {
        color: ${props.isLightColor ? black(props) : white(props)} !important;
      }

      // カレンダー部分
      .MuiPickersDay-dayWithMargin {
        background-color: ${
          props.isLightColor
            ? white(props)
            : 'rgba(255, 255, 255, 0.1)' ?? 'initial'
        } !important;
        color: ${props.isLightColor ? black(props) : white(props)} !important;

        :hover {
          background-color: ${white(props)} !important;
          color: ${black(props)} !important;
        }
        &.Mui-selected {
          background-color: ${white(props)} !important;
          color: ${black(props)} !important;
          ${
            props.isLightColor &&
            css`
              border: 1px solid ${gray04(props)} !important;
            `
          };
        }
        &.Mui-disabled {
          background-color: initial !important;
          color: ${
            props.isLightColor
              ? 'rgba(0, 0, 0, 0.3)'
              : 'rgba(255, 255, 255, 0.3)'
          } !important;
        }
      }
    `}
  ${mediaMax.tablet} {
    margin-bottom: 0;
  }
`;
const DatePickerContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  ${mediaMax.tablet} {
    width: 100%;
  }
`;
const TimeZoneContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  ${mediaMax.tablet} {
    padding: 0;
  }
`;
const TimeZoneIcon = styled(Icon)<{
  isFillColor: boolean;
  isLightColor: boolean;
}>`
  color: ${gray01};
  margin-right: ${spacing(8)};
  font-size: ${fontSize(18)};

  ${(props) =>
    props.isFillColor &&
    css`
      color: ${props.isLightColor ? black(props) : white(props)};
    `}
`;
const TimeZoneSelect = styled(TextField)<{
  color: string;
  isFillColor: boolean;
  isLightColor: boolean;
}>`
  border: 0;
  width: 160px;
  color: ${({ isFillColor, isLightColor }) =>
    isFillColor ? (isLightColor ? black : white) : gray01};
  font-size: ${fontSize(14)};
  ${(props) =>
    props.isFillColor &&
    css`
      background-color: initial;

      .MuiSelect-icon {
        color: ${props.isLightColor ? black(props) : white(props)};
      }
    `}

  ${(props) => css`
    .MuiInput-underline:before {
      border: 0;
      border-color: ${props.isFillColor
        ? props.isLightColor
          ? black(props)
          : white(props)
        : props.color};
    }
    .MuiInput-underline:hover:not(.Mui-disabled):before {
      border-color: ${props.isFillColor
        ? props.isLightColor
          ? black(props)
          : white(props)
        : props.color};
    }
    .MuiInput-underline:after {
      border-color: ${props.isFillColor
        ? props.isLightColor
          ? black(props)
          : white(props)
        : props.color};
    }
    .MuiSvgIcon-root {
      color: ${props.isLightColor ? black(props) : white(props)};
    }
  `}
`;

const PickerContainer = styled.div`
  width: 50%;
  height: 100%;
  display: flex;
  align-items: top;
  flex: 1;
`;
const StyledTimeList = styled.div`
  display: block;
  width: 100%;
  height: 100%;
  overflow: auto;
`;
const TimeListContainer = styled.div<{
  isFillColor: boolean;
}>`
  ${(props) =>
    props.isFillColor
      ? `
    padding: ${spacing(24)} ${spacing(48)};
  `
      : `
    padding: 0 0 0 ${spacing(40)};
  `}
  ${mediaMax.tablet} {
    padding: ${spacing(16)};
  }
`;
const DateSelected = styled.div`
  padding-bottom: ${spacing(24)};
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(20)};
  text-align: center;
  position: sticky;
  top: 0;
  left: 0;
  right: 0;
  z-index: ${zIndex(200)};
  background-color: ${white};
  ${mediaMax.tablet} {
    font-size: ${fontSize(22)};
  }
`;

const NoTimeListDay = styled.button<{
  color: string;
  isLightColor: boolean;
  isFillColor: boolean;
  isSelected: boolean;
  isHostDiagnosticMode?: boolean;
}>`
  margin: 2px;
  width: 40px;
  height: 40px;
  border: 1px dashed ${gray02};
  border-radius: 50%;

  ${(props) =>
    props.isFillColor &&
    !props.isLightColor &&
    css`
      color: ${white(props)};
      border: 1px dashed ${white(props)};
    `}
  ${(props) =>
    props.isSelected &&
    css`
      border: none;
      color: ${props.isFillColor
        ? black(props)
        : props.isLightColor
        ? black(props)
        : white(props)};
      background-color: ${props.isFillColor ? white(props) : props.color || ''};
    `}

  ${(props) =>
    props.isHostDiagnosticMode &&
    !props.isSelected &&
    css`
      cursor: pointer;
      background-color: ${props.isFillColor ? props.color || '' : white(props)};
    `}

  :hover {
    ${(props) => css`
      border: none;
      color: ${props.isFillColor
        ? black(props)
        : props.isLightColor
        ? black(props)
        : white(props)};
      background-color: ${props.isFillColor ? white(props) : props.color || ''};
    `}
  }

  ${mediaMax.smartphone} {
    width: 36px;
    height: 36px;
  }
`;
